import React, { useState } from "react";
import { ProgrammingLanguage } from "../../Interface/Index";
import Settings from "./Settings";
import Editor from "react-simple-code-editor";
import {
  highlight,
  languages as prismLanguages,
} from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism-tomorrow.css";
import { Button, Card } from "antd";
import { getSnippetByLanguage } from "../../Utils/language-snippets";
import codeService from "../../Services/code.service";
import { CodeBlock } from "react-code-blocks";
import "./index.scss";

interface CodeEditorProps {
  languages: ProgrammingLanguage[];
  setCompiling: (compiling: { loading: boolean; message: string }) => void;
  handleFullScreen: () => void;
  isFullScreen: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>, value: string) => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  languages,
  setCompiling,
  handleFullScreen,
  isFullScreen,
  onChange,
}) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<ProgrammingLanguage | null>(null);
  const [code, setCode] = useState("");
  const [output, setOutput] = useState<string | null>(null);

  const handleSelectLanguage = (language: ProgrammingLanguage | null) => {
    if (!language) {
      setCode("");
      setSelectedLanguage(null);
      return;
    }
    setSelectedLanguage(language);
    const snippet = getSnippetByLanguage(language.name);
    setCode(snippet);
  };

  const onCodeChange = (newCode: string) => {
    setCode(newCode);
    onChange({} as any, newCode);
  };

  const handleRunCodeClick = async () => {
    if (!selectedLanguage) return;

    setCompiling({ loading: true, message: "Compiling..." });
    try {
      const output = await codeService.compileCode(code, selectedLanguage?.id);
      if (output) {
        console.log("🚀 ~ handleRunCodeClick ~ output:", output);
        setOutput(output);
      } else {
        setOutput("Error occurred while running the code.");
      }
    } catch (error: any) {
      console.log("🚀 ~ handleRunCode ~ error:", error);
    } finally {
      setCompiling({ loading: false, message: "" });
    }
  };

  return (
    <Card bordered={true} style={{ height: "100%", borderColor: "#000000" }}>
      <div style={{ height: "100%", display: "flex", flexDirection: "column"}}>
        <div style={{ flex: "0 0 50px" }}>
          <Settings
            languages={languages}
            handleSelectLanguage={handleSelectLanguage}
            selectedLanguage={selectedLanguage}
            handleFullScreen={handleFullScreen}
            isFullScreen={isFullScreen}
          />
        </div>

        <div className="code-editor-container">
          <Editor
            value={code}
            onValueChange={onCodeChange}
            highlight={(code) => highlight(code, prismLanguages.js)}
            padding={10}
            className="code-editor"
            placeholder={
              !selectedLanguage ? "Please select a programming language" : ""
            }
            disabled={!selectedLanguage}
          />

          <div className="output-container">
            <h3 className="output-title-md" style={{ color: "#000000" }}>
              Output
            </h3>
            <CodeBlock
              customStyle={{
                padding: "0px",
                margin: "0px",
                height: "100%",
                width: "100%",
                background: "none",
              }}
              text={output ? output : "Run the code to see the output"}
              language="javascript"
              showLineNumbers={false}
            />
          </div>
        </div>

        <div style={{ marginTop: "10px" }}>
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#56AA30",
              paddingInline: "50px",
            }}
            onClick={handleRunCodeClick}
            disabled={!selectedLanguage}
          >
            Run
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CodeEditor;
